import React, { ReactNode, Ref } from 'react';
import FormGroup from './FormGroup';
import Label from './Label';
import ErrorMessage from './ErrorMessage';
import Hint from './Hint';
import CharacterCount from './CharacterCount';
import CharacterCountMessage from './CharacterCountMessage';
import FormNote from './FormNote';
import { getGlobalFormConfig, useFormConfig } from './FormConfigContext';

export interface FormControlWrapperProps {
  id: string;
  children: ReactNode;
  label?: ReactNode;
  labelId?: string;
  labelHint?: ReactNode;
  labelHintId?: string;
  errorId?: string;
  errorMessage?: string;
  hint?: string;
  hintId?: string;
  note?: ReactNode;
  success?: boolean;
  characterCountMax?: number;
  characterCount?: number;
  characterCountId?: string;
  required?: boolean;
  requiredLabel?: ReactNode;
  formGroupRef?: Ref<HTMLDivElement>;
  ungroup?: boolean;
}

/**
 * Utility component to add common form features to any input:
 *  - CharacterCount
 *  - FormGroup
 *  - Label
 *  - Hints
 *  - ErrorMessage
 *  - Note
 */
const FormControlWrapper = ({
  id,
  label,
  labelId,
  labelHint,
  labelHintId,
  errorId,
  errorMessage,
  hint,
  hintId,
  note,
  characterCountMax,
  characterCount,
  characterCountId,
  children,
  required,
  requiredLabel,
  formGroupRef,
  ungroup,
}: FormControlWrapperProps): JSX.Element => {
  const Wrapper = characterCountMax ? CharacterCount : React.Fragment;
  const { requiredLabel: contextRequiredLabel } = useFormConfig();
  const { requiredLabel: globalRequiredLabel } = getGlobalFormConfig();
  const finalRequiredLabel = requiredLabel || contextRequiredLabel || globalRequiredLabel;
  const contents = (
    <>
      {label ? (
        <Label htmlFor={id} error={errorId && errorMessage} id={labelId}>
          {label}
          {required ? <> {finalRequiredLabel}</> : null}
          {labelHint ? (
            <>
              {' '}
              <Hint id={labelHintId}>{labelHint}</Hint>
            </>
          ) : null}
        </Label>
      ) : null}
      {hint && hintId ? <Hint id={hintId}>{hint}</Hint> : null}
      {errorId && errorMessage ? <ErrorMessage id={errorId}>{errorMessage}</ErrorMessage> : null}
      {children}
      {note ? <FormNote>{note}</FormNote> : null}
    </>
  );
  return (
    <Wrapper>
      {ungroup ? (
        contents
      ) : (
        <FormGroup error={errorId && errorMessage} ref={formGroupRef}>
          {contents}
        </FormGroup>
      )}
      {characterCountMax && characterCountId ? (
        <CharacterCountMessage
          id={characterCountId}
          characterCountMax={characterCountMax}
          characterCount={characterCount || 0}
        />
      ) : null}
    </Wrapper>
  );
};

export default FormControlWrapper;
